import { ClockCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { differenceInCalendarDays, differenceInHours, differenceInMinutes, isWithinInterval } from 'date-fns';
import { t } from 'i18next';
import { NavLink } from 'react-router-dom';
import { InviteIcon, CalendarIcon, ListCheckIcon, HeartbeatIcon, Tag, Text, GameIcon } from 'ui';
import { type Todo } from '../../../types';

type TodosWidgetItemTimeTagProperties = {
  endTime: Date;
  startTime: Date;
  hasInProgressStatus: boolean;
};

type P = {
  todoItemData: Todo;
};

const TodosWidgetItemTimeTag = ({ endTime, startTime, hasInProgressStatus }: TodosWidgetItemTimeTagProperties) => {
  const isInProgress = isWithinInterval(new Date(), { start: startTime, end: endTime });
  const remainingDays = differenceInCalendarDays(endTime, new Date());
  const remainingHours = differenceInHours(endTime, new Date());
  const remainingMinutes = differenceInMinutes(endTime, new Date());

  const handleRemainingTime = () => {
    if (remainingDays > 3) {
      return t(`days_left_more`);
    }

    if (remainingDays >= 1) {
      return t(`days_left`, {
        count: remainingDays,
        days: remainingDays,
      });
    }

    return t(`time_left`, {
      count: remainingHours,
      hours: remainingHours % 60,
      minutes: remainingMinutes % 60,
    });
  };

  const handleRemainingTimeInProgress = () => {
    return t(`Hours and minutes left`, {
      count: remainingHours,
      minutes: remainingMinutes % 60,
      hours: remainingHours,
    });
  };

  const timeTagType =
    hasInProgressStatus && isInProgress
      ? {
          timeString: handleRemainingTimeInProgress(),
          color: 'warning',
          icon: <PlayCircleOutlined />,
        }
      : {
          timeString: handleRemainingTime(),
          color: 'default',
          icon: <ClockCircleOutlined />,
        };

  return (
    <Tag className="todos-widget-item__time-tag" icon={timeTagType.icon} color={timeTagType.color}>
      {timeTagType.timeString}
    </Tag>
  );
};

const activityTodoTitle = {
  annually: t('Complete your annual activity'),
  daily: t('Complete your daily activity'),
  monthly: t('Complete your monthly activity'),
  quarterly: t('Complete your quarterly activity'),
  weekly: t('Complete your weekly activity'),
  default: t('Complete the activity'),
};

const handleTodoItemAttributes = (todoItemData: Todo) => {
  const iconDefaultClassName = 'todos-widget-item__info__icon';

  if (todoItemData.event) {
    const isInvitationPending = todoItemData.event.currentUserRsvp === 'pending';

    return {
      icon: isInvitationPending ? (
        <InviteIcon className={iconDefaultClassName} />
      ) : (
        <CalendarIcon className={iconDefaultClassName} />
      ),
      description: todoItemData.event.name,
      title: isInvitationPending ? t('Respond to event invitation') : t('Join the event'),
    };
  }

  if (todoItemData.activity) {
    return {
      icon: <HeartbeatIcon className={iconDefaultClassName} />,
      description: todoItemData.activity.name,
      title: activityTodoTitle[todoItemData.activity.periodicityType] ?? activityTodoTitle.default,
    };
  }

  if (todoItemData.poll) {
    return {
      icon: <ListCheckIcon className={iconDefaultClassName} />,
      description: todoItemData.poll.description,
      title: t('Complete the poll'),
    };
  }

  if (todoItemData.withingsOrderPost) {
    return {
      icon: <GameIcon className={iconDefaultClassName} />,
      description: todoItemData.withingsOrderPost?.textContent,
      title: t('Order your Withings device'),
    };
  }

  return null;
};

const TodosWidgetItem = ({ todoItemData }: P) => {
  const todoItemAttributes = handleTodoItemAttributes(todoItemData);
  const hasInProgressStatus =
    todoItemData.event?.currentUserRsvp === 'going' ||
    (todoItemData.activity?.periodicityType === 'daily' && todoItemData.activity.times.length > 0);

  return (
    <NavLink<unknown> to={todoItemData.path} className="todos-widget-item">
      <div className="todos-widget-item__info">
        {todoItemAttributes?.icon}
        <Text item={{ strong: true, ellipsis: true, className: 'todos-widget-item__info__text' }}>
          {todoItemAttributes?.title}
        </Text>
      </div>

      <Text item={{ ellipsis: true }}>{todoItemAttributes?.description}</Text>
      <TodosWidgetItemTimeTag
        endTime={todoItemData.endTime}
        startTime={todoItemData.startTime}
        hasInProgressStatus={hasInProgressStatus}
      />
    </NavLink>
  );
};

export default TodosWidgetItem;
